'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { captureMessage } from '@sentry/nextjs';
import { useRouter } from 'next/navigation';

import { subscribeToNewsletter } from '@/actions/subscribeToNewsletter';
import { EVENT_NAMES, trackEvent } from '@/analytics';
import {
  FormStateType,
  ReCaptchaForm,
  ReCaptchaFormOnSubmitProps,
} from '@/components/FormElements';
import { RECAPTCHA_ACTION_NAME_SUBSCRIBE } from '@/constants';
import { CONTACT_PAGE_PATH } from '@/helpers/linkHelpers';
import { getFormOrigin } from '@/lib/1440api/getFormOrigin';
import {
  TranslatedAPIMessageSentimentType,
  translateAPIMessage,
} from '@/lib/1440api/translateAPIMessage';
import { HUMANIZED_API_RESPONSES } from '@/lib/1440api/translateAPIMessage';
import { useSession } from '@/providers/SessionProvider/SessionProvider';

interface NewsletterSubscribeFormContextProps {
  email: string;
  setEmail: (email: string) => void;
  formState: FormStateType;
  responseSentiment?: TranslatedAPIMessageSentimentType;
  formMessage: string;
  showContactLink: boolean;
  contactPath: string;
  inputProps: {
    placeholder: string;
    value: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    id: string;
    name: string;
    required: boolean;
    type: HTMLInputElement['type'];
  };
}

export const NewsletterSubscribeFormContext = createContext<
  NewsletterSubscribeFormContextProps | undefined
>(undefined);

export interface NewsletterSubscribeFormProviderProps {
  children: ReactNode;
  locationOnPage: string;
  listID: string;
  formID: string;
}

export function useNewsletterSubscribeForm() {
  const context = useContext(NewsletterSubscribeFormContext);

  if (!context) {
    throw new Error(
      'useNewsletterSubscribeForm must be used within a NewsletterSubscribeFormProvider',
    );
  }

  return context;
}

export function NewsletterSubscribeFormProvider({
  children,
  locationOnPage,
  listID,
  formID,
}: NewsletterSubscribeFormProviderProps) {
  const [email, setEmail] = useState('');
  const [formOriginURL, setFormOriginURL] = useState('');
  const [formState, setFormState] = useState<FormStateType>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [showContactLink, setShowContactLink] = useState(false);
  const [responseSentiment, setResponseSentiment] = useState<
    TranslatedAPIMessageSentimentType | undefined
  >();
  const { utmParamsString } = useSession();
  const router = useRouter();

  useEffect(() => {
    const locationURL = window.location?.href;
    const originURLString = getFormOrigin(locationURL, locationOnPage);

    setFormOriginURL(originURLString || '');
  }, [locationOnPage]);

  async function handleSubmit({ formData }: ReCaptchaFormOnSubmitProps) {
    setFormState('loading');

    // filter params
    const paramKeysToInclude = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_1440_exp',
      'gclid',
      'fbclid',
      'twclid',
      'li_did',
      'ttclid',
    ];
    const filterParams = new URLSearchParams(utmParamsString);
    filterParams.forEach((value, key) => {
      if (!paramKeysToInclude.includes(key)) {
        filterParams.delete(key, value);
      }
    });

    // get form data from RecaptchaForm wrapper and append additional data
    formData.append('search-params', filterParams.toString());
    formData.append('origin', formOriginURL);
    formData.append('list', listID);

    // make subscribe call to API
    try {
      const { message, success, emx, ffuid } = await subscribeToNewsletter({
        formData,
      });

      setFormState('responded');
      setFormMessage(message.humanMessage);
      setShowContactLink(message.addContactLink);
      setResponseSentiment(message.sentiment);

      if (success) {
        // Track Newsletter Signup event in server-side GTM
        trackEvent(EVENT_NAMES.newsletter_signup, {
          signup_success: success.toString(),
          list: listID,
        });

        // Track Conversion event in client-side GTM
        trackEvent(
          EVENT_NAMES.subscription_complete,
          {
            email: formData.get('email') as string,
            origin: formOriginURL,
            list: listID,
            utm_source: filterParams.get('utm_source') || '',
            utm_medium: filterParams.get('utm_medium') || '',
            utm_campaign: filterParams.get('utm_campaign') || '',
            utm_term: filterParams.get('utm_term') || '',
            utm_1440_exp: filterParams.get('utm_1440_exp') || '',
            gclid: filterParams.get('gclid') || '',
            li_did: filterParams.get('li_did') || '',
            fbclid: filterParams.get('fbclid') || '',
            ttclid: filterParams.get('ttclid') || '',
            twclid: filterParams.get('twclid') || '',
            ffuid: ffuid,
          },
          ['gtm', 'ss-gtm'],
        );

        // Router Redirect
        router.push('/thanks-for-joining?list=' + listID + '&emx=' + emx);
      }
    } catch (error) {
      console.error('subscribeToNewsletter action error', error);
      captureMessage('subscribeToNewsletter action error', {
        level: 'error',
        extra: {
          error_message: error,
        },
      });

      setFormState('responded');

      const message = translateAPIMessage(undefined);
      setFormMessage(message.humanMessage);
      setShowContactLink(message.addContactLink);
      setResponseSentiment(message.sentiment);
    }
  }

  function getContactPath(listID: string) {
    if (typeof window === 'undefined') return '';

    const params = new URLSearchParams();
    const translatedResponses = HUMANIZED_API_RESPONSES;

    if (formMessage === translatedResponses.blocked) {
      params.append('_c_reason', 'cannot_subscribe');

      if (listID) {
        params.append('_c_list', listID);
      }
    }

    if (email) {
      params.append('_c_email', email);
    }

    const paramsString = params.toString();

    return `${CONTACT_PAGE_PATH}${paramsString ? '?' + paramsString : ''}`;
  }

  return (
    <NewsletterSubscribeFormContext.Provider
      value={{
        email,
        setEmail,
        formState,
        responseSentiment,
        formMessage,
        showContactLink,
        contactPath: getContactPath(listID),
        inputProps: {
          placeholder: 'Your Email',
          value: email,
          onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(event.target.value),
          id: 'subscribe-form-email-input',
          name: 'email',
          required: true,
          type: 'text',
        },
      }}
    >
      <ReCaptchaForm
        onSubmit={handleSubmit}
        reCaptchaActionName={RECAPTCHA_ACTION_NAME_SUBSCRIBE}
        id={formID}
      >
        {children}
      </ReCaptchaForm>
    </NewsletterSubscribeFormContext.Provider>
  );
}
